import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import ConfigSection from "./ConfigSection";
import "./ConfigPage.css";
import { useSystemConfigWS } from "../../hooks/useSystemConfigWS";
import { notify } from "../../utils/notify";
import { ICONS } from "../../icons";
import Button from "../UI/Button";
import PageHeader from "../UI/PageHeader";
import EntityTranslationsButton from "../UI/EntityTranslationsButton";
import { I18nContext } from "../../context/I18nContext";

const configSchema = {
    general: [
        { key: "system_name", type: "text", label: "System Name", public: true, system: false, secure: false },
        // Shown in the browser tab's meta description (see MetaDescriptionSync
        // in App.js) and, like system_name, translatable per-language via its
        // own EntityTranslationsButton below. Empty by default — see this
        // field's own comment in internal/config/seed.go for why.
        { key: "system_description", type: "textarea", label: "Meta Description", public: true, system: false, secure: false },
        {
            key: "system_timezone", type: "select", label: "System Timezone", options: [
                "Africa/Abidjan (+00:00)",
                "Africa/Accra (+00:00)",
                "Africa/Addis_Ababa (+03:00)",
                "Africa/Algiers (+01:00)",
                "Africa/Asmara (+03:00)",
                "Africa/Bamako (+00:00)",
                "Africa/Bangui (+01:00)",
                "Africa/Banjul (+00:00)",
                "Africa/Bissau (+00:00)",
                "Africa/Blantyre (+02:00)",
                "Africa/Brazzaville (+01:00)",
                "Africa/Bujumbura (+02:00)",
                "Africa/Cairo (+02:00)",
                "Africa/Casablanca (+01:00)",
                "Africa/Ceuta (+01:00)",
                "Africa/Conakry (+00:00)",
                "Africa/Dakar (+00:00)",
                "Africa/Dar_es_Salaam (+03:00)",
                "Africa/Djibouti (+03:00)",
                "Africa/Douala (+01:00)",
                "Africa/El_Aaiun (+01:00)",
                "Africa/Freetown (+00:00)",
                "Africa/Gaborone (+02:00)",
                "Africa/Harare (+02:00)",
                "Africa/Johannesburg (+02:00)",
                "Africa/Juba (+02:00)",
                "Africa/Kampala (+03:00)",
                "Africa/Khartoum (+02:00)",
                "Africa/Kigali (+02:00)",
                "Africa/Kinshasa (+01:00)",
                "Africa/Lagos (+01:00)",
                "Africa/Libreville (+01:00)",
                "Africa/Lome (+00:00)",
                "Africa/Luanda (+01:00)",
                "Africa/Lubumbashi (+02:00)",
                "Africa/Lusaka (+02:00)",
                "Africa/Malabo (+01:00)",
                "Africa/Maputo (+02:00)",
                "Africa/Maseru (+02:00)",
                "Africa/Mbabane (+02:00)",
                "Africa/Mogadishu (+03:00)",
                "Africa/Monrovia (+00:00)",
                "Africa/Nairobi (+03:00)",
                "Africa/Ndjamena (+01:00)",
                "Africa/Niamey (+01:00)",
                "Africa/Nouakchott (+00:00)",
                "Africa/Ouagadougou (+00:00)",
                "Africa/Porto-Novo (+01:00)",
                "Africa/Sao_Tome (+00:00)",
                "Africa/Tripoli (+02:00)",
                "Africa/Tunis (+01:00)",
                "Africa/Windhoek (+02:00)",
                "America/Adak (-10:00)",
                "America/Anchorage (-09:00)",
                "America/Anguilla (-04:00)",
                "America/Antigua (-04:00)",
                "America/Araguaina (-03:00)",
                "America/Argentina/Buenos_Aires (-03:00)",
                "America/Argentina/Catamarca (-03:00)",
                "America/Argentina/Cordoba (-03:00)",
                "America/Argentina/Jujuy (-03:00)",
                "America/Argentina/La_Rioja (-03:00)",
                "America/Argentina/Mendoza (-03:00)",
                "America/Argentina/Rio_Gallegos (-03:00)",
                "America/Argentina/Salta (-03:00)",
                "America/Argentina/San_Juan (-03:00)",
                "America/Argentina/San_Luis (-03:00)",
                "America/Argentina/Tucuman (-03:00)",
                "America/Argentina/Ushuaia (-03:00)",
                "America/Aruba (-04:00)",
                "America/Asuncion (-03:00)",
                "America/Atikokan (-05:00)",
                "America/Bahia (-03:00)",
                "America/Bahia_Banderas (-06:00)",
                "America/Barbados (-04:00)",
                "America/Belem (-03:00)",
                "America/Belize (-06:00)",
                "America/Blanc-Sablon (-04:00)",
                "America/Boa_Vista (-04:00)",
                "America/Bogota (-05:00)",
                "America/Boise (-07:00)",
                "America/Cambridge_Bay (-07:00)",
                "America/Campo_Grande (-04:00)",
                "America/Cancun (-05:00)",
                "America/Caracas (-04:00)",
                "America/Cayenne (-03:00)",
                "America/Cayman (-05:00)",
                "America/Chicago (-06:00)",
                "America/Chihuahua (-06:00)",
                "America/Ciudad_Juarez (-07:00)",
                "America/Costa_Rica (-06:00)",
                "America/Creston (-07:00)",
                "America/Cuiaba (-04:00)",
                "America/Curacao (-04:00)",
                "America/Danmarkshavn (+00:00)",
                "America/Dawson (-07:00)",
                "America/Dawson_Creek (-07:00)",
                "America/Denver (-07:00)",
                "America/Detroit (-05:00)",
                "America/Dominica (-04:00)",
                "America/Edmonton (-07:00)",
                "America/Eirunepe (-05:00)",
                "America/El_Salvador (-06:00)",
                "America/Fort_Nelson (-07:00)",
                "America/Fortaleza (-03:00)",
                "America/Glace_Bay (-04:00)",
                "America/Goose_Bay (-04:00)",
                "America/Grand_Turk (-05:00)",
                "America/Grenada (-04:00)",
                "America/Guadeloupe (-04:00)",
                "America/Guatemala (-06:00)",
                "America/Guayaquil (-05:00)",
                "America/Guyana (-04:00)",
                "America/Halifax (-04:00)",
                "America/Havana (-05:00)",
                "America/Hermosillo (-07:00)",
                "America/Indiana/Indianapolis (-05:00)",
                "America/Indiana/Knox (-06:00)",
                "America/Indiana/Marengo (-05:00)",
                "America/Indiana/Petersburg (-05:00)",
                "America/Indiana/Tell_City (-06:00)",
                "America/Indiana/Vevay (-05:00)",
                "America/Indiana/Vincennes (-05:00)",
                "America/Indiana/Winamac (-05:00)",
                "America/Inuvik (-07:00)",
                "America/Iqaluit (-05:00)",
                "America/Jamaica (-05:00)",
                "America/Juneau (-09:00)",
                "America/Kentucky/Louisville (-05:00)",
                "America/Kentucky/Monticello (-05:00)",
                "America/Kralendijk (-04:00)",
                "America/La_Paz (-04:00)",
                "America/Lima (-05:00)",
                "America/Los_Angeles (-08:00)",
                "America/Lower_Princes (-04:00)",
                "America/Maceio (-03:00)",
                "America/Managua (-06:00)",
                "America/Manaus (-04:00)",
                "America/Marigot (-04:00)",
                "America/Martinique (-04:00)",
                "America/Matamoros (-06:00)",
                "America/Mazatlan (-07:00)",
                "America/Menominee (-06:00)",
                "America/Merida (-06:00)",
                "America/Metlakatla (-09:00)",
                "America/Mexico_City (-06:00)",
                "America/Miquelon (-03:00)",
                "America/Moncton (-04:00)",
                "America/Monterrey (-06:00)",
                "America/Montevideo (-03:00)",
                "America/Montserrat (-04:00)",
                "America/Nassau (-05:00)",
                "America/New_York (-05:00)",
                "America/Nome (-09:00)",
                "America/Noronha (-02:00)",
                "America/North_Dakota/Beulah (-06:00)",
                "America/North_Dakota/Center (-06:00)",
                "America/North_Dakota/New_Salem (-06:00)",
                "America/Nuuk (-02:00)",
                "America/Ojinaga (-06:00)",
                "America/Panama (-05:00)",
                "America/Paramaribo (-03:00)",
                "America/Phoenix (-07:00)",
                "America/Port-au-Prince (-05:00)",
                "America/Port_of_Spain (-04:00)",
                "America/Porto_Velho (-04:00)",
                "America/Puerto_Rico (-04:00)",
                "America/Punta_Arenas (-03:00)",
                "America/Rankin_Inlet (-06:00)",
                "America/Recife (-03:00)",
                "America/Regina (-06:00)",
                "America/Resolute (-06:00)",
                "America/Rio_Branco (-05:00)",
                "America/Santarem (-03:00)",
                "America/Santiago (-03:00)",
                "America/Santo_Domingo (-04:00)",
                "America/Sao_Paulo (-03:00)",
                "America/Scoresbysund (-01:00)",
                "America/Sitka (-09:00)",
                "America/St_Barthelemy (-04:00)",
                "America/St_Johns (-03:30)",
                "America/St_Kitts (-04:00)",
                "America/St_Lucia (-04:00)",
                "America/St_Thomas (-04:00)",
                "America/St_Vincent (-04:00)",
                "America/Swift_Current (-06:00)",
                "America/Tegucigalpa (-06:00)",
                "America/Thule (-04:00)",
                "America/Tijuana (-08:00)",
                "America/Toronto (-05:00)",
                "America/Tortola (-04:00)",
                "America/Vancouver (-08:00)",
                "America/Whitehorse (-07:00)",
                "America/Winnipeg (-06:00)",
                "America/Yakutat (-09:00)",
                "Antarctica/Casey (+11:00)",
                "Antarctica/Davis (+07:00)",
                "Antarctica/DumontDUrville (+10:00)",
                "Antarctica/Macquarie (+11:00)",
                "Antarctica/Mawson (+05:00)",
                "Antarctica/McMurdo (+13:00)",
                "Antarctica/Palmer (-03:00)",
                "Antarctica/Rothera (-03:00)",
                "Antarctica/Syowa (+03:00)",
                "Antarctica/Troll (+00:00)",
                "Antarctica/Vostok (+06:00)",
                "Arctic/Longyearbyen (+01:00)",
                "Asia/Aden (+03:00)",
                "Asia/Almaty (+06:00)",
                "Asia/Amman (+03:00)",
                "Asia/Anadyr (+12:00)",
                "Asia/Aqtau (+05:00)",
                "Asia/Aqtobe (+05:00)",
                "Asia/Ashgabat (+05:00)",
                "Asia/Atyrau (+05:00)",
                "Asia/Baghdad (+03:00)",
                "Asia/Bahrain (+03:00)",
                "Asia/Baku (+04:00)",
                "Asia/Bangkok (+07:00)",
                "Asia/Barnaul (+07:00)",
                "Asia/Beirut (+02:00)",
                "Asia/Bishkek (+06:00)",
                "Asia/Brunei (+08:00)",
                "Asia/Chita (+09:00)",
                "Asia/Choibalsan (+08:00)",
                "Asia/Colombo (+05:30)",
                "Asia/Damascus (+03:00)",
                "Asia/Dhaka (+06:00)",
                "Asia/Dili (+09:00)",
                "Asia/Dubai (+04:00)",
                "Asia/Dushanbe (+05:00)",
                "Asia/Famagusta (+02:00)",
                "Asia/Gaza (+02:00)",
                "Asia/Hebron (+02:00)",
                "Asia/Ho_Chi_Minh (+07:00)",
                "Asia/Hong_Kong (+08:00)",
                "Asia/Hovd (+07:00)",
                "Asia/Irkutsk (+08:00)",
                "Asia/Jakarta (+07:00)",
                "Asia/Jayapura (+09:00)",
                "Asia/Jerusalem (+02:00)",
                "Asia/Kabul (+04:30)",
                "Asia/Kamchatka (+12:00)",
                "Asia/Karachi (+05:00)",
                "Asia/Kathmandu (+05:45)",
                "Asia/Khandyga (+09:00)",
                "Asia/Kolkata (+05:30)",
                "Asia/Krasnoyarsk (+07:00)",
                "Asia/Kuala_Lumpur (+08:00)",
                "Asia/Kuching (+08:00)",
                "Asia/Kuwait (+03:00)",
                "Asia/Macau (+08:00)",
                "Asia/Magadan (+11:00)",
                "Asia/Makassar (+08:00)",
                "Asia/Manila (+08:00)",
                "Asia/Muscat (+04:00)",
                "Asia/Nicosia (+02:00)",
                "Asia/Novokuznetsk (+07:00)",
                "Asia/Novosibirsk (+07:00)",
                "Asia/Omsk (+06:00)",
                "Asia/Oral (+05:00)",
                "Asia/Phnom_Penh (+07:00)",
                "Asia/Pontianak (+07:00)",
                "Asia/Pyongyang (+09:00)",
                "Asia/Qatar (+03:00)",
                "Asia/Qostanay (+06:00)",
                "Asia/Qyzylorda (+05:00)",
                "Asia/Riyadh (+03:00)",
                "Asia/Sakhalin (+11:00)",
                "Asia/Samarkand (+05:00)",
                "Asia/Seoul (+09:00)",
                "Asia/Shanghai (+08:00)",
                "Asia/Singapore (+08:00)",
                "Asia/Srednekolymsk (+11:00)",
                "Asia/Taipei (+08:00)",
                "Asia/Tashkent (+05:00)",
                "Asia/Tbilisi (+04:00)",
                "Asia/Tehran (+03:30)",
                "Asia/Thimphu (+06:00)",
                "Asia/Tokyo (+09:00)",
                "Asia/Tomsk (+07:00)",
                "Asia/Ulaanbaatar (+08:00)",
                "Asia/Urumqi (+06:00)",
                "Asia/Ust-Nera (+10:00)",
                "Asia/Vientiane (+07:00)",
                "Asia/Vladivostok (+10:00)",
                "Asia/Yakutsk (+09:00)",
                "Asia/Yangon (+06:30)",
                "Asia/Yekaterinburg (+05:00)",
                "Asia/Yerevan (+04:00)",
                "Atlantic/Azores (-01:00)",
                "Atlantic/Bermuda (-04:00)",
                "Atlantic/Canary (+00:00)",
                "Atlantic/Cape_Verde (-01:00)",
                "Atlantic/Faroe (+00:00)",
                "Atlantic/Madeira (+00:00)",
                "Atlantic/Reykjavik (+00:00)",
                "Atlantic/South_Georgia (-02:00)",
                "Atlantic/St_Helena (+00:00)",
                "Atlantic/Stanley (-03:00)",
                "Australia/Adelaide (+10:30)",
                "Australia/Brisbane (+10:00)",
                "Australia/Broken_Hill (+10:30)",
                "Australia/Darwin (+09:30)",
                "Australia/Eucla (+08:45)",
                "Australia/Hobart (+11:00)",
                "Australia/Lindeman (+10:00)",
                "Australia/Lord_Howe (+11:00)",
                "Australia/Melbourne (+11:00)",
                "Australia/Perth (+08:00)",
                "Australia/Sydney (+11:00)",
                "Europe/Amsterdam (+01:00)",
                "Europe/Andorra (+01:00)",
                "Europe/Astrakhan (+04:00)",
                "Europe/Athens (+02:00)",
                "Europe/Belgrade (+01:00)",
                "Europe/Berlin (+01:00)",
                "Europe/Bratislava (+01:00)",
                "Europe/Brussels (+01:00)",
                "Europe/Bucharest (+02:00)",
                "Europe/Budapest (+01:00)",
                "Europe/Busingen (+01:00)",
                "Europe/Chisinau (+02:00)",
                "Europe/Copenhagen (+01:00)",
                "Europe/Dublin (+00:00)",
                "Europe/Gibraltar (+01:00)",
                "Europe/Guernsey (+00:00)",
                "Europe/Helsinki (+02:00)",
                "Europe/Isle_of_Man (+00:00)",
                "Europe/Istanbul (+03:00)",
                "Europe/Jersey (+00:00)",
                "Europe/Kaliningrad (+02:00)",
                "Europe/Kirov (+03:00)",
                "Europe/Kyiv (+02:00)",
                "Europe/Lisbon (+00:00)",
                "Europe/Ljubljana (+01:00)",
                "Europe/London (+00:00)",
                "Europe/Luxembourg (+01:00)",
                "Europe/Madrid (+01:00)",
                "Europe/Malta (+01:00)",
                "Europe/Mariehamn (+02:00)",
                "Europe/Minsk (+03:00)",
                "Europe/Monaco (+01:00)",
                "Europe/Moscow (+03:00)",
                "Europe/Oslo (+01:00)",
                "Europe/Paris (+01:00)",
                "Europe/Podgorica (+01:00)",
                "Europe/Prague (+01:00)",
                "Europe/Riga (+02:00)",
                "Europe/Rome (+01:00)",
                "Europe/Samara (+04:00)",
                "Europe/San_Marino (+01:00)",
                "Europe/Sarajevo (+01:00)",
                "Europe/Saratov (+04:00)",
                "Europe/Simferopol (+03:00)",
                "Europe/Skopje (+01:00)",
                "Europe/Sofia (+02:00)",
                "Europe/Stockholm (+01:00)",
                "Europe/Tallinn (+02:00)",
                "Europe/Tirane (+01:00)",
                "Europe/Ulyanovsk (+04:00)",
                "Europe/Vaduz (+01:00)",
                "Europe/Vatican (+01:00)",
                "Europe/Vienna (+01:00)",
                "Europe/Vilnius (+02:00)",
                "Europe/Volgograd (+03:00)",
                "Europe/Warsaw (+01:00)",
                "Europe/Zagreb (+01:00)",
                "Europe/Zurich (+01:00)",
                "Indian/Antananarivo (+03:00)",
                "Indian/Chagos (+06:00)",
                "Indian/Christmas (+07:00)",
                "Indian/Cocos (+06:30)",
                "Indian/Comoro (+03:00)",
                "Indian/Kerguelen (+05:00)",
                "Indian/Mahe (+04:00)",
                "Indian/Maldives (+05:00)",
                "Indian/Mauritius (+04:00)",
                "Indian/Mayotte (+03:00)",
                "Indian/Reunion (+04:00)",
                "Pacific/Apia (+13:00)",
                "Pacific/Auckland (+13:00)",
                "Pacific/Bougainville (+11:00)",
                "Pacific/Chatham (+13:45)",
                "Pacific/Chuuk (+10:00)",
                "Pacific/Easter (-05:00)",
                "Pacific/Efate (+11:00)",
                "Pacific/Fakaofo (+13:00)",
                "Pacific/Fiji (+12:00)",
                "Pacific/Funafuti (+12:00)",
                "Pacific/Galapagos (-06:00)",
                "Pacific/Gambier (-09:00)",
                "Pacific/Guadalcanal (+11:00)",
                "Pacific/Guam (+10:00)",
                "Pacific/Honolulu (-10:00)",
                "Pacific/Kanton (+13:00)",
                "Pacific/Kiritimati (+14:00)",
                "Pacific/Kosrae (+11:00)",
                "Pacific/Kwajalein (+12:00)",
                "Pacific/Majuro (+12:00)",
                "Pacific/Marquesas (-09:30)",
                "Pacific/Midway (-11:00)",
                "Pacific/Nauru (+12:00)",
                "Pacific/Niue (-11:00)",
                "Pacific/Norfolk (+12:00)",
                "Pacific/Noumea (+11:00)",
                "Pacific/Pago_Pago (-11:00)",
                "Pacific/Palau (+09:00)",
                "Pacific/Pitcairn (-08:00)",
                "Pacific/Pohnpei (+11:00)",
                "Pacific/Port_Moresby (+10:00)",
                "Pacific/Rarotonga (-10:00)",
                "Pacific/Saipan (+10:00)",
                "Pacific/Tahiti (-10:00)",
                "Pacific/Tarawa (+12:00)",
                "Pacific/Tongatapu (+13:00)",
                "Pacific/Wake (+12:00)",
                "Pacific/Wallis (+12:00)",
                "UTC (+00:00)"
            ], public: true, system: false, secure: false
        },
        { key: "language", type: "select", label: "Language", options: ["English", "Arabic", "French"], public: true, system: false, secure: false },
        { key: "date_format", type: "select", label: "Date Format", options: ["YYYY-MM-DD", "DD-MM-YYYY"], public: true, system: false, secure: false },
        { key: "enable_maintenance_mode", type: "checkbox", label: "Enable Maintenance Mode", public: true, system: false, secure: false },
        { key: "auto_backup_frequency", type: "number", label: "Auto Backup Frequency (days)", public: true, system: false, secure: false },
    ],
    session: [
        { key: "inactivity_timeout", type: "number", label: "Inactivity Timeout (minutes)", public: true, system: false, secure: false },
        { key: "token_expiry_duration", type: "number", label: "Token Expiry Duration (minutes)", public: true, system: false, secure: false },
        { key: "remember_me_duration", type: "number", label: "Remember Me Duration (days)", public: true, system: false, secure: false },
        { key: "auto_logout_warning", type: "number", label: "Auto Logout Warning (minutes)", public: true, system: false, secure: false },
    ],
    password_policy: [
        { key: "min_length", type: "number", label: "Minimum Length", public: true, system: false, secure: false },
        { key: "require_lowercase", type: "checkbox", label: "Require Lowercase", public: true, system: false, secure: false },
        { key: "require_uppercase", type: "checkbox", label: "Require Uppercase", public: true, system: false, secure: false },
        { key: "require_numbers", type: "checkbox", label: "Require Numbers", public: true, system: false, secure: false },
        { key: "require_symbols", type: "checkbox", label: "Require Symbols", public: true, system: false, secure: false },
        { key: "password_expiry_days", type: "number", label: "Password Expiry Days", public: true, system: false, secure: false },
        { key: "password_history_limit", type: "number", label: "Password History Limit", public: true, system: false, secure: false },
    ],
    accounts: [
        { key: "max_failed_attempts", type: "number", label: "Max Failed Attempts", public: true, system: false, secure: false },
        { key: "account_lock_duration", type: "number", label: "Account Lock Duration (minutes)", public: true, system: false, secure: false },
        { key: "enable_captcha_after_fail", type: "checkbox", label: "Enable Captcha After Fail", public: true, system: false, secure: false },
        { key: "enable_auto_deactivation", type: "checkbox", label: "Enable Auto Deactivation", public: true, system: false, secure: false },
        { key: "auto_deactivation_days", type: "number", label: "Auto Deactivation Days", public: true, system: false, secure: false },
    ],
    notifications: [
        { key: "enable_low_stock_alerts", type: "checkbox", label: "Enable Low Stock Alerts", public: true, system: false, secure: false },
        { key: "system_email", type: "text", label: "System Email", public: true, system: false, secure: false },
        { key: "enable_login_alerts", type: "checkbox", label: "Enable Login Alerts", public: true, system: false, secure: false },
        { key: "notify_on_lockout", type: "checkbox", label: "Notify on Lockout", public: true, system: false, secure: false },
        { key: "smtp_server", type: "text", label: "SMTP Server", public: true, system: false, secure: false },
        { key: "smtp_port", type: "number", label: "SMTP Port", public: true, system: false, secure: false },
        { key: "smtp_username", type: "text", label: "SMTP Username", public: true, system: false, secure: false },
        { key: "smtp_password", type: "password", label: "SMTP Password", public: true, system: false, secure: false },
    ],
    appearance: [
        { key: "theme_color", type: "color", label: "Theme Color", public: true, system: false, secure: false },
        { key: "logo", type: "file", label: "Logo", public: true, system: false, secure: false },
        { key: "favicon", type: "file", label: "Favicon", public: true, system: false, secure: false },
        { key: "sidebar_style", type: "select", label: "Sidebar Style", options: ["expanded", "collapsed"], public: true, system: false, secure: false },
        { key: "dark_mode", type: "checkbox", label: "Dark Mode", public: true, system: false, secure: false },
    ],

    pagination: [
        { key: "limits", type: "json_map", label: "Routes Limits Settings", public: true, system: true, secure: false },
    ],
    api_integrations: [
        { key: "google_api_key", type: "text", label: "Google API Key", public: true, system: false, secure: false },
        { key: "aws_access_key", type: "text", label: "AWS Access Key", public: true, system: false, secure: false },
        { key: "s3_bucket_name", type: "text", label: "S3 Bucket Name", public: true, system: false, secure: false },
        { key: "sms_gateway_api", type: "text", label: "SMS Gateway API", public: true, system: false, secure: false },
        { key: "webhook_endpoint", type: "text", label: "Webhook Endpoint", public: true, system: false, secure: false },
    ],
    inventory: [
        { key: "allow_negative_stock", type: "checkbox", label: "Allow Negative Stock", public: true, system: false, secure: false },
    ],
    invoicing: [
        { key: "invoice_prefix", type: "text", label: "Invoice Number Prefix", public: true, system: false, secure: false },
        // 0 (or left blank) means no zero-padding — the sequence is just
        // appended to the prefix as-is. Any positive value is the fixed
        // number of digits the sequence is padded to (e.g. 4 -> 0001).
        { key: "invoice_padding_digits", type: "number", label: "Sequence Digits (0 = no padding)", public: true, system: false, secure: false },
    ],
    loyalty: [
        { key: "enabled", type: "checkbox", label: "Enable Loyalty Program", public: true, system: false, secure: false },
        { key: "earn_on_spend", type: "checkbox", label: "Earn Points on Amount Spent", public: true, system: false, secure: false },
        { key: "earn_on_product", type: "checkbox", label: "Earn Points per Product", public: true, system: false, secure: false },
        // Spend-based earn rate: pointsPerAmount points for every amountPerPoints spent.
        { key: "points_per_amount", type: "number", label: "Points Earned", public: true, system: false, secure: false },
        { key: "amount_per_points", type: "number", label: "Per Amount Spent", public: true, system: false, secure: false },
        // Redemption: how much ONE point is worth in currency when converted to wallet balance.
        { key: "point_value", type: "number", label: "Point Value When Redeemed (currency per point)", public: true, system: false, secure: false },
        { key: "min_redeem_points", type: "number", label: "Minimum Points Balance Required to Redeem", public: true, system: false, secure: false },
    ],
    returns: [
        // 0 (or left blank) means a return is allowed at any time, no
        // matter how old the sale is — see services.ReturnWindowDays.
        { key: "return_window_days", type: "number", label: "Return Window (days, 0 = unlimited)", public: true, system: false, secure: false },
        // 0 (or left blank) means a voucher issued as return credit stays
        // open-ended — no expiry date at all. A positive value sets that
        // voucher's ExpiresAt to (issue date + this many days) — see
        // services.ReturnVoucherExpiryDays. Only affects a return-issued
        // voucher; one sold directly via Checkout's "Sell Voucher" never
        // expires regardless of this setting.
        { key: "voucher_expiry_days", type: "number", label: "Return Voucher Expiry (days, 0 = never expires)", public: true, system: false, secure: false },
    ],
    vouchers: [
        // Mirrors invoicing.invoice_prefix exactly — prepended to the
        // zero-padded sequence number, e.g. "VCH-" -> "VCH-000001".
        // Blank falls back to "VCH-" (see services.NextVoucherCode).
        { key: "code_prefix", type: "text", label: "Voucher Code Prefix", public: true, system: false, secure: false },
        // Off (default): a Purchase Voucher's balance can be spent across
        // more than one future purchase. On: it must be spent in a single
        // transaction — a purchase smaller than the balance then prompts
        // the cashier to either forfeit the remainder or add more items
        // first. See services.VoucherRequireFullRedemption.
        { key: "require_full_redemption", type: "checkbox", label: "Require Full Redemption per Purchase", public: true, system: false, secure: false },
    ],
    // Offline mode (this feature's own plan doc, "Real Offline Mode for the
    // Cashier/Checkout Screen") — public:true is required here, not just
    // convention: OfflineContext.js reads this value via GET
    // /api/public-config, which only ever returns public/non-secure rows
    // (see GetPublicConfig in config_handler.go), since a cashier session
    // has no config_view permission to call the authenticated /api/config
    // endpoint this screen itself uses. Mirrors services.OfflineMaxHours's
    // own default (24) — see that function's doc comment for why 0 isn't
    // treated as "no limit" here the way it is for returns/vouchers above.
    pos: [
        { key: "offline_max_hours", type: "number", label: "Max Offline Duration (hours)", public: true, system: false, secure: false },
    ],
    // Real payment gateway integration — Network International's N-Genius
    // Online (UAE) and Paymob's Intention API (Egypt); see
    // internal/services/payment_gateway.go on the backend. Every row here
    // is seeded Public:false/Secure:true-where-sensitive (unlike
    // api_integrations above) — a "password"-type field renders whatever
    // GetAll returned, which is config.MaskedSecretValue ("••••••••") once
    // a secret has ever been saved, not the real value (see
    // config_manager.go's GetAll masking); leaving it exactly as shown and
    // saving keeps the existing secret untouched (config_handler.go skips
    // overwriting it), so only clear it and type a new value when you
    // actually mean to change it.
    payment_gateways: [
        { key: "ngenius_enabled", type: "checkbox", label: "Enable Network International (N-Genius)", public: false, system: true, secure: false },
        { key: "ngenius_environment", type: "select", label: "N-Genius Environment", options: ["sandbox", "production"], public: false, system: true, secure: false },
        { key: "ngenius_api_key", type: "password", label: "N-Genius API Key", public: false, system: true, secure: true },
        { key: "ngenius_outlet_reference", type: "text", label: "N-Genius Outlet Reference", public: false, system: true, secure: false },
        { key: "ngenius_realm_name", type: "text", label: "N-Genius Realm Name", public: false, system: true, secure: false },
        { key: "ngenius_currency_code", type: "text", label: "N-Genius Currency Code (e.g. AED)", public: false, system: true, secure: false },
        { key: "ngenius_webhook_header_name", type: "text", label: "N-Genius Webhook Header Name", public: false, system: true, secure: false },
        { key: "ngenius_webhook_header_value", type: "password", label: "N-Genius Webhook Shared Secret", public: false, system: true, secure: true },
        { key: "paymob_enabled", type: "checkbox", label: "Enable Paymob", public: false, system: true, secure: false },
        { key: "paymob_secret_key", type: "password", label: "Paymob Secret Key", public: false, system: true, secure: true },
        { key: "paymob_public_key", type: "password", label: "Paymob Public Key", public: false, system: true, secure: true },
        { key: "paymob_integration_id_card", type: "password", label: "Paymob Card Integration ID", public: false, system: true, secure: true },
        { key: "paymob_hmac_secret", type: "password", label: "Paymob HMAC Secret", public: false, system: true, secure: true },
        { key: "paymob_currency_code", type: "text", label: "Paymob Currency Code (e.g. EGP)", public: false, system: true, secure: false },
        // public_base_url is this deployment's own publicly-reachable
        // address — required by BOTH gateways to build their
        // webhook/redirect URLs (see services.GatewayCallbackURLs); a
        // gateway checkout refuses to start until this is set.
        { key: "public_base_url", type: "text", label: "Public Base URL (e.g. https://pos.example.com)", public: false, system: true, secure: false },
        { key: "payment_timeout_minutes", type: "number", label: "Payment Timeout (minutes)", public: false, system: true, secure: false },
    ],
};

export default function ConfigPage() {
    const navigate = useNavigate();
    // system_name can come back translated per the signed-in user's own
    // language (resolved server-side, see applyTranslatedSystemName in
    // config_handler.go) — the /config fetch below needs to re-run
    // whenever languageCode changes, or a language switch would silently
    // keep showing the name resolved under the PREVIOUS language until the
    // next full page reload.
    const { languageCode } = useContext(I18nContext);
    const [activeCategory, setActiveCategory] = useState("general");
    const [formData, setFormData] = useState({});
    const [metaState, setMetaState] = useState({});
    // Row IDs (from GetAll's own new "id" field — see config_manager.go)
    // keyed the same way as formData/metaState, only ever used so far to
    // give the "general.system_name" field its own EntityTranslationsButton
    // below (that button needs the SystemSetting row's real primary key,
    // not just its value).
    const [settingIds, setSettingIds] = useState({});
    const [isReady, setIsReady] = useState(false);

    // جلب الإعدادات عند فتح الصفحة
    useEffect(() => {
        api.get("/config")
            .then((res) => {
                const parsed = {};
                const meta = {};
                const ids = {};

                // نمر على الـ Schema لضمان وجود الهيكل حتى لو السيرفر مبعتش بيانات لـ category معينة
                Object.keys(configSchema).forEach((category) => {
                    parsed[category] = {};
                    meta[category] = {};
                    ids[category] = {};

                    const serverFields = res.data[category] || {};

                    configSchema[category].forEach((f) => {
                        const fieldData = serverFields[f.key];
                        let val = fieldData?.value;

                        if (f.type === "json_map") {
                            try {
                                // إذا كانت القيمة نصاً، نحولها لكائن، إذا لم تكن موجودة نضع كائن فارغ
                                val = val ? (typeof val === 'string' ? JSON.parse(val) : val) : {};
                            } catch (e) {
                                console.error("Error parsing json_map:", e);
                                val = {};
                            }
                        } 
                        // --- بقية التحويلات القديمة ---
                        else if (val === "true" || val === true) val = true;

                        // تحويل الأنواع
                        //if (val === "true" || val === true) val = true;
                        else if (val === "false" || val === false) val = false;
                        else if (!isNaN(val) && val !== "" && val !== null && f.type === "number") val = Number(val);
                        else if (val === undefined || val === null) val = f.type === "checkbox" ? false : "";

                        parsed[category][f.key] = val;
                        ids[category][f.key] = fieldData?.id;

                        // تخزين الـ Meta (أولوية للسيرفر ثم السكيما)
                        meta[category][f.key] = {
                            public: fieldData?.public ?? f.public ?? false,
                            system: fieldData?.system ?? f.system ?? false,
                            secure: fieldData?.secure ?? f.secure ?? false,
                        };
                    });
                });

                setFormData(parsed);
                setMetaState(meta);
                setSettingIds(ids);
                setIsReady(true); // ⚡ تفعيل الجاهزية هنا
            })
            .catch((err) => {
                console.error("Failed to fetch config:", err.message);
                setIsReady(true); // نلغي التحميل حتى لو فشل
            });
    }, [languageCode]);

    // تحديثات الـ WebSocket
    useSystemConfigWS((msg) => {
        setFormData((prev) => ({
            ...prev,
            [msg.category]: {
                ...prev[msg.category],
                [msg.key]: msg.value,
            },
        }));
    });

    const handleChange = (category, key, value) => {
        setFormData((prev) => ({
            ...prev,
            [category]: { ...prev[category], [key]: value },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const normalPayload = [];
        const fileFormData = new FormData();
        let hasFile = false;

        Object.keys(formData).forEach((category) => {
            Object.keys(formData[category]).forEach((key) => {
                const value = formData[category][key];
                const fieldDef = configSchema[category]?.find(f => f.key === key);
                const fieldMeta = metaState[category]?.[key] || fieldDef || {};

                let type = "string";
                let finalValue = value;

                // --- الإضافة هنا ---
if (fieldDef?.type === "json_map") {
    finalValue = JSON.stringify(value || {}); // تحويل الكائن لنص JSON
    type = "string"; // النوع في الداتابيز يبقى string
}

                else if (typeof value === "boolean" || fieldDef?.type === "checkbox") {
                    type = "bool";
                    finalValue = !!value;
                } else if (fieldDef?.type === "number" && !isNaN(Number(value)) && value !== "") {
                    type = "int";
                    finalValue = Number(value);
                }

                if (value instanceof File) {
                    hasFile = true;
                    fileFormData.append(`${category}.${key}`, value);
                } else {
                    normalPayload.push({
                        category,
                        key,
                        value: finalValue,
                        type,
                        public: fieldMeta.public ?? false,
                        system: fieldMeta.system ?? false,
                        secure: fieldMeta.secure ?? false,
                    });
                }
            });
        });

        try {
            await api.post("/config", normalPayload);
            if (hasFile) {
                // No explicit Content-Type here on purpose — axios detects a
                // FormData body and sets "multipart/form-data;
                // boundary=..." itself, WITH the random boundary string the
                // browser actually used to separate the form's parts.
                // Hardcoding "multipart/form-data" (no boundary) here used
                // to override that and send a Content-Type the backend's
                // c.MultipartForm() parser couldn't make sense of — every
                // upload came back as a generic save failure regardless of
                // the image itself being perfectly fine.
                await api.post("/config/upload", fileFormData);
            }
            notify.success("Settings saved successfully! 🎉");
        } catch (err) {
            notify.error("Failed to save settings ❌");
        }
    };

    // ⚡ الحل لمنع القيم الفارغة عند الريفرش
    if (!isReady) {
        return <div style={{ padding: "20px" }}>Loading Configuration...</div>;
    }

    return (
        <div className="config-page" style={{ display: "flex", gap: "1rem" }}>

            {/* borderInlineEnd بدل borderRight عشان الخط الفاصل بين قائمة
                الفئات والمحتوى يفضل في الجهة الصح (يتقلب تلقائيًا مع اتجاه اللغة) */}
            <aside style={{ width: "220px", padding: "10px", borderInlineEnd: "1px solid #ccc" }}>
                <h3 style={{ marginBottom: "10px" }}>Categories</h3>
                {Object.keys(configSchema).map((cat) => (
                    <button
                        key={cat}
                        onClick={() => setActiveCategory(cat)}
                        className={activeCategory === cat ? "active" : ""}
                        style={{
                            display: "block",
                            width: "100%",
                            marginBottom: "0.5rem",
                            //background: activeCategory === cat ? "#007bff" : "#f0f0f0",
                            //color: activeCategory === cat ? "#fff" : "#000",
                            border: "none",
                            padding: "0.5rem",
                            textAlign: "start",
                            borderRadius: "4px",
                            cursor: "pointer",
                        }}
                    >
                        {cat.replace("_", " ").toUpperCase()}
                    </button>
                ))}
            </aside>
            
            <div style={{ flex: 1, padding: "10px" }}>
                {/* Header Section - متناسق مع بقية الصفحات */}

                






                
            <PageHeader className="flex flex-col md:flex-row md:items-center justify-between gap-6 border-b border-gray-100 pb-6">
                <div className="flex items-center gap-4">
                    <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
                        <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
                    </button>
                    <div className="flex items-center justify-center min-w-[48px] h-[48px]" style={{ color: "var(--primary-color)" }}>
                        <ICONS.Settings size={40} className="shrink-0" />
                    </div>
                    <div>
                        <h2 className="text-xl md:text-2xl font-bold" style={{ color: "var(--primary-color)" }}>System Configuration</h2>
                        <p className="text-gray-400 text-xs md:text-sm font-medium">Manage global limits and application behavior</p>
                    </div>
                </div>

                <div className="flex flex-col sm:flex-row items-center gap-3 w-full md:w-auto">

                    <Button variant="primary" icon={ICONS.Save} onClick={handleSubmit} type="submit" className="w-full sm:w-auto">
Save Settings
</Button>
                    </div>
            </PageHeader>
            
            <form onSubmit={handleSubmit} >
                <h2 style={{ marginBottom: "10px" }}>
                    {activeCategory.replace("_", " ").toUpperCase()}
                </h2>

                <ConfigSection
                    category={activeCategory}
                    schema={configSchema[activeCategory]}
                    data={formData[activeCategory]}
                    metaData={metaState[activeCategory]}
                    onChange={handleChange}
                    onMetaChange={(cat, key, updatedMeta) => {
                        setMetaState(prev => ({
                            ...prev,
                            [cat]: { ...prev[cat], [key]: updatedMeta }
                        }));
                    }}
                    sideActions={activeCategory === "general" ? {
                        system_name: settingIds.general?.system_name && (
                            <EntityTranslationsButton
                                entityType="system_setting"
                                entityId={settingIds.general.system_name}
                                defaultName={formData.general?.system_name}
                                label="Translate"
                            />
                        ),
                        system_description: settingIds.general?.system_description && (
                            <EntityTranslationsButton
                                entityType="system_setting"
                                entityId={settingIds.general.system_description}
                                defaultName={formData.general?.system_description}
                                label="Translate"
                            />
                        ),
                    } : {}}
                />



                {/* <button
                    type="submit"
                    style={{
                        marginTop: "15px",
                        width: "160px",
                        padding: "0.6rem",
                        background: "#007bff",
                        color: "#fff",
                        border: "none",
                        borderRadius: "5px",
                        cursor: "pointer",
                    }}
                >
                    💾 Save Settings
                </button> */}
            </form>
            </div>
        </div>
    );
}